import { CalendarDate, Time } from '@internationalized/date';
import { maybe, rd, RemoteData } from '@passionware/monads';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { zipObject } from 'lodash';
import { CallsheetApi } from 'v5/api/callsheet/callsheet.api';
import { Booking, Production } from '../../../../__types__';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { WithBookingService } from '../BookingService/BookingService';
import {
  extractBookingIdsFromProductions,
  generateCallsheetDataFromProductions,
  generateCallsheetDataFromProductionsForRange
} from './_private/generateCallsheetDataFromProductions';
import { CallsheetService } from './CallsheetService';

type SingleOptions = { selectedDate: CalendarDate; callTime?: Time | null };
type RangeOptions = {
  dateRange: { from: CalendarDate; to: CalendarDate };
  callTime?: Time | null;
};

export function createCallsheetService(
  config: WithServices<
    [WithErrorService, WithMessageService, WithBookingService]
  > & {
    api: CallsheetApi;
    queryClient: QueryClient;
  }
): CallsheetService {
  const {
    api,
    queryClient,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory('CallsheetService');

  const computeCountsByProduction = (
    productionsRecord: Record<Production['id'], RemoteData<Production>>,
    options: SingleOptions | RangeOptions
  ): Record<Production['id'], RemoteData<number | null>> => {
    const allProductions = Object.values(productionsRecord)
      .map(rd.tryGet)
      .filter(
        (production): production is Production => production !== undefined
      );

    const bookingIds = Array.from(
      new Set(extractBookingIdsFromProductions(allProductions))
    );
    const bookingsRecord =
      config.services.bookingService.useBookingsById(bookingIds);

    return Object.entries(productionsRecord).reduce(
      (acc, [id, productionData]) => {
        const productionId = Number(id);
        const production = rd.tryGet(productionData);
        if (rd.isIdle(productionData)) {
          acc[productionId] = rd.ofIdle();
          return acc;
        }
        if (rd.isPending(productionData)) {
          acc[productionId] = rd.ofPending();
          return acc;
        }
        if (rd.isError(productionData) || !production) {
          acc[productionId] = rd.of(null);
          return acc;
        }

        const productionBookingIds = Array.from(
          new Set(extractBookingIdsFromProductions([production]))
        );
        const productionBookingsRd = rd.combine(
          productionBookingIds.reduce(
            (bookingAcc, bookingId) => {
              bookingAcc[bookingId] = bookingsRecord[bookingId] ?? rd.ofIdle();
              return bookingAcc;
            },
            {} as Record<Booking['id'], RemoteData<Booking>>
          )
        );

        const countRd = rd
          .journey(productionBookingsRd)
          .wait(rd.ofPending())
          .catch(() => rd.of<number | null>(null))
          .map(productionBookings => {
            const generatedData =
              'dateRange' in options
                ? generateCallsheetDataFromProductionsForRange(
                    [production],
                    productionBookings,
                    options
                  )
                : generateCallsheetDataFromProductions(
                    [production],
                    productionBookings,
                    options
                  );

            return rd.of<number | null>(
              generatedData.meta.teamMemberCountByProductionId[
                productionId
              ] ?? 0
            );
          });
        acc[productionId] = countRd;

        return acc;
      },
      {} as Record<Production['id'], RemoteData<number | null>>
    );
  };

  // Subscribe to mutation cache to watch for successful callsheet mutations
  // When v4 callsheet mutations succeed, invalidate v5 callsheet queries
  // V4 mutation keys: ['callsheets', 'mutation', 'create|update|delete|...']
  // V5 keys: ['callsheet', 'list', ...] and ['callsheet', 'detail', id]
  queryClient.getMutationCache().subscribe(event => {
    if (
      event?.type === 'updated' &&
      event.mutation.state.status === 'success'
    ) {
      const mutationKey = event.mutation.options.mutationKey;

      // Check if this is a v4 callsheet mutation
      if (
        Array.isArray(mutationKey) &&
        mutationKey[0] === 'callsheets' &&
        mutationKey[1] === 'mutation'
      ) {
        // Invalidate all v5 callsheet queries when any callsheet mutation succeeds
        void queryClient.invalidateQueries({
          queryKey: ['callsheet']
        });
      }
    }
  });

  // Subscribe to reportSystemEffect messages to invalidate queries when callsheet effects are reported
  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'callsheet': {
          // First, invalidate all list queries
          queryClient.removeQueries({
            queryKey: ['callsheet', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['callsheet', 'list'],
            type: 'active'
          });
          // Then invalidate the detail query
          await queryClient.invalidateQueries({
            queryKey: ['callsheet', 'detail', effect.entityId]
          });
        }
      }
    }
    message.sendResponse();
  });

  return {
    useCallsheets: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: ['callsheet', 'list', query],
            queryFn: interceptor.wrap(() =>
              maybe.map(query, q => api.searchCallsheets(q))
            )
          },
          queryClient
        )
      ),
    ensureCallsheets: query =>
      queryClient.fetchQuery({
        queryKey: ['callsheet', 'list', query],
        queryFn: interceptor.wrap(() => api.searchCallsheets(query))
      }),
    useCallsheet: id =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            enabled: maybe.isPresent(id),
            queryKey: ['callsheet', 'detail', id],
            queryFn: interceptor.wrap(async () => {
              const callsheet = await api.getCallsheet(id, ''); // workspace is not used in the API
              if (!callsheet) {
                throw new Error(`Callsheet ${id} not found`);
              }
              return callsheet;
            })
          },
          queryClient
        )
      ),
    ensureCallsheet: async id => {
      const callsheet = await queryClient.fetchQuery({
        queryKey: ['callsheet', 'detail', id],
        queryFn: interceptor.wrap(async () => {
          const result = await api.getCallsheet(id, '');
          if (!result) {
            throw new Error(`Callsheet ${id} not found`);
          }
          return result;
        })
      });
      return callsheet;
    },
    useGeneratedCallsheetData: (
      productionsRecord: Record<Production['id'], RemoteData<Production>>,
      options: { selectedDate: CalendarDate; callTime?: Time | null }
    ) => {
      // Combine all productions RemoteData into a single RemoteData<Production[]>
      const productionsArray = Object.values(productionsRecord);
      const combinedProductions = rd.combineAll(productionsArray);

      const relevantBookingIds = rd.useMemoMap(
        combinedProductions,
        extractBookingIdsFromProductions
      );

      const bookingsResponse = config.services.bookingService.useBookingsById(
        rd.getOrElse(relevantBookingIds, [])
      );

      const combined = rd.combine({
        productionsData: combinedProductions,
        bookingsResponse: rd.combine(bookingsResponse)
      });

      // Include options in dependency by mapping to a combined object
      const combinedWithOptions = rd.map(combined, data => ({
        ...data,
        options
      }));

      return rd.useMemoMap(
        combinedWithOptions,
        ({ productionsData, bookingsResponse, options: opts }) => {
          return generateCallsheetDataFromProductions(
            productionsData,
            bookingsResponse,
            opts
          );
        }
      );
    },
    generateCallsheetDataFromProductions: async (productions, options) => {
      const relevantBookingIds = extractBookingIdsFromProductions(productions);
      const bookingsResponse = zipObject(
        relevantBookingIds,
        await Promise.all(
          relevantBookingIds.map(id =>
            config.services.bookingService.ensureBooking(id)
          )
        )
      );
      return generateCallsheetDataFromProductions(
        productions,
        bookingsResponse,
        options
      );
    },
    useGeneratedCallsheetDataForRange: (
      productionsRecord: Record<Production['id'], RemoteData<Production>>,
      options: {
        dateRange: { from: CalendarDate; to: CalendarDate };
        callTime?: Time | null;
      }
    ) => {
      // Combine all productions RemoteData into a single RemoteData<Production[]>
      const productionsArray = Object.values(productionsRecord);
      const combinedProductions = rd.combineAll(productionsArray);

      const relevantBookingIds = rd.useMemoMap(
        combinedProductions,
        extractBookingIdsFromProductions
      );

      const bookingsResponse = config.services.bookingService.useBookingsById(
        rd.getOrElse(relevantBookingIds, [])
      );

      const combined = rd.combine({
        productionsData: combinedProductions,
        bookingsResponse: rd.combine(bookingsResponse)
      });

      // Include options in dependency by mapping to a combined object
      const combinedWithOptions = rd.map(combined, data => ({
        ...data,
        options
      }));

      return rd.useMemoMap(
        combinedWithOptions,
        ({ productionsData, bookingsResponse, options: opts }) => {
          return generateCallsheetDataFromProductionsForRange(
            productionsData,
            bookingsResponse,
            opts
          );
        }
      );
    },
    useGeneratedCallsheetCountsByProduction: computeCountsByProduction,
    useGeneratedCallsheetCountsByProductionForRange: computeCountsByProduction,
    generateCallsheetDataFromProductionsForRange: async (
      productions,
      options
    ) => {
      const relevantBookingIds = extractBookingIdsFromProductions(productions);
      const bookingsResponse = zipObject(
        relevantBookingIds,
        await Promise.all(
          relevantBookingIds.map(id =>
            config.services.bookingService.ensureBooking(id)
          )
        )
      );
      return generateCallsheetDataFromProductionsForRange(
        productions,
        bookingsResponse,
        options
      );
    }
  };
}
